<template>
  <article class="cmp cmp-product-thumb" :data-testing="item.variation.id">
    <div :class="paddingClasses" :style="paddingInlineStyles">
      <add-to-basket
        data-testing="item-add-to-basket"
        :variation-id="item.variation.id"
        :is-salable="!!item.filter && item.filter.isSalable"
        :has-children="!!item.item && item.item.salableVariationCount > 1"
        :interval-quantity="item.variation.intervalOrderQuantity || 1"
        :minimum-quantity="item.variation.minimumOrderQuantity"
        :maximum-quantity="!!item.variation.maximumOrderQuantity && item.variation.maximumOrderQuantity > 0 ? item.variation.maximumOrderQuantity : null"
        :order-properties="
          item.properties.filter(function(prop) {
            return prop.property.isOderProperty;
          })
        "
        :has-order-properties="item.hasOrderProperties"
        :has-required-order-property="item.hasRequiredOrderProperty"
        :use-large-scale="true"
        :show-quantity="false"
        :item-url="item | itemURL(urlWithVariationId)"
        :has-price="item | hasItemDefaultPrice"
        :has-graduated-price="itemGraduatedPriceisCheapestSorting || itemGraduatedPricesalableVariationCount"
        :item-type="item.item.itemType"
      >
      </add-to-basket>

      <div class="thumb-image">
        <div class="prop-1-1">
          <slot name="item-image">
            <category-image-carousel
              :image-urls-data="item.images | itemImages(imageUrlAccessor)"
              :alt="item | itemName"
              :title="item | itemName"
              :item-url="item | itemURL(urlWithVariationId)"
              :enable-carousel="$ceres.config.item.enableImageCarousel"
              :disable-carousel-on-mobile="disableCarouselOnMobile"
              ref="categoryImageCarousel"
            >
            </category-image-carousel>
          </slot>
        </div>
      </div>

      <!-- STORE SPECIALS -->
      <slot name="store-special">
        <item-store-special
          v-if="storeSpecial || item.variation.bundleType === 'bundle' || item.item.itemType === 'set'"
          :store-special="storeSpecial"
          :recommended-retail-price="item.prices.rrp"
          :variation-retail-price="item.prices.default"
          :special-offer-price="item.prices.specialOffer"
          :decimal-count="decimalCount"
          :bundle-type="item.variation.bundleType"
          :item-type="item.item.itemType"
        >
        </item-store-special>
      </slot>
      <!-- ./STORE SPECIALS -->

      <!-- ITEM DETAILS -->
      <slot name="item-details">
        <div class="thumb-content">
          <a :href="item | itemURL(urlWithVariationId)" class="thumb-title font-weight-bold" :class="{ 'stretched-link': $ceres.config.global.shippingCostsCategoryId == 0 }">
            {{ item | itemName }}
            <!----><span v-for="attribute in item.groupedAttributes">{{ "Ceres::Template.itemGroupedAttribute" | translate(attribute) }}</span>
          </a>

          <div class="addinfo-wrapper">
            <span v-if="item.texts.name2 !== 'N / A'" class="d-flex small mt-1 align-items-start"><i class="fa fa-check mr-1 mt-1" aria-hidden="true"></i> {{ item.texts.name2 }}</span>

            <div class="d-flex small mt-1 align-items-start" v-if="!(item.unit.unitOfMeasurement === 'C62' && item.unit.content === 1)">
              <i class="fa fa-check mr-1 mt-1" aria-hidden="true"></i>
              <span>{{ item.unit.content }}</span>
              <span>&nbsp;{{ item.unit.names.name }}</span>
              <span v-if="item.variation.mayShowUnitPrice">&nbsp;| {{ basePrice }}</span>
            </div>

            <div class="d-flex small my-1 align-items-start" v-if="item.texts.name3 != '' && item.texts.name3 !== 'N / A'">
              <i class="fa fa-check mr-1 mt-1" aria-hidden="true"></i> {{ item.texts.name3 }}
            </div>

            <div class="d-flex small align-items-start flex-wrap" v-if="item.texts.shortDescription.length > 0">
              <strong>Einsatzgebiet:&nbsp;</strong>
              <span v-html="item.texts.shortDescription"></span>
            </div>
          </div>

          <div class="category-lowest-price small" v-if="item.prices.default.lowestPrice.value && hasCrossPrice">
            <span v-html="$translate('Ceres::Template.itemLowestPrice', { price: item.prices.default.lowestPrice.formatted })"></span>
          </div>

          <div class="vat small text-muted mt-2 d-none">
            {{ $translate("Ceres::Template.itemFootnote") }} <span v-if="showNetPrices">{{ $translate("Ceres::Template.itemExclVAT") }}</span>
            <span v-else>{{ $translate("Ceres::Template.itemInclVAT") }}</span>
            {{ $translate("Ceres::Template.itemExclusive") }}
            <a v-if="$ceres.config.global.shippingCostsCategoryId > 0" data-toggle="modal" href="#shippingscosts" class="text-appearance" :title="$translate('Ceres::Template.itemShippingCosts')">{{
              $translate("Ceres::Template.itemShippingCosts")
            }}</a>
            <a v-else :title="$translate('Ceres::Template.itemShippingCosts')">{{ $translate("Ceres::Template.itemShippingCosts") }}</a>
          </div>

          <div class="d-flex align-items-end mt-3">
            <div class="availability-preview small font-weight-bold">
              <span :class="'circle availability_' + item.variation.availability.id"></span>
              <span>{{ item.variation.availability.names.name }}</span>
            </div>
            <div class="thumb-meta">
              <slot name="before-prices"></slot>

              <div class="prices">
                <div v-if="item.prices.rrp && item.prices.rrp.price.value > 0 && item.prices.rrp.price.value > item.prices.default.price.value" class="price-view-port">
                  <del class="crossprice" v-if="item.prices.specialOffer">
                    {{ item.prices.default.unitPrice.formatted | itemCrossPrice(true) }}
                  </del>
                  <del class="crossprice" v-else>
                    {{ item.prices.rrp.unitPrice.formatted | itemCrossPrice }}
                  </del>
                </div>

                <div class="price text-right">
                  <template v-if="item.item.itemType === 'set'"> {{ $translate("Ceres::Template.itemSetPrice", { price: itemSetPrice }) }}</template>
                  <template v-else-if="itemGraduatedPriceisCheapestSorting">
                    {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }}
                  </template>
                  <template v-else-if="itemGraduatedPricesalableVariationCount">
                    {{ $translate("Ceres::Template.itemFromPrice", { price: itemPriceGraduated }) }}
                  </template>
                  <template v-else> {{ item.prices.default.unitPrice.formatted | specialOffer(item.prices, "unitPrice", "formatted") }}</template>
                </div>
              </div>
            </div>
            <slot name="after-prices"></slot>
          </div>
        </div>
      </slot>
      <!-- ./ITEM DETAILS  -->
    </div>
  </article>
</template>

<script>
import { mapState } from "vuex";
import CategoryImageCarousel from "./CategoryImageCarousel.vue";
import ItemStoreSpecial from "./ItemStoreSpecial.vue";
import { getSlotData } from "../../helper/getSlotData";

export default {
  components: {
    CategoryImageCarousel,
    ItemStoreSpecial,
  },

  props: {
    decimalCount: {
      type: Number,
      default: 0,
    },
    imageUrlAccessor: {
      type: String,
      default: "urlMiddle",
    },
    itemData: {
      type: Object,
    },
    disableCarouselOnMobile: {
      type: Boolean,
    },
    paddingClasses: {
      type: String,
      default: null,
    },
    paddingInlineStyles: {
      type: String,
      default: null,
    },
    forceUrlWithVariationId: {
      type: Boolean,
      default: false,
    },
  },

  jsonDataFields: ["itemDataRef"],

  methods: {
    formatValues(values) {
      // Prüfen, ob `values` existiert und ein Array ist
      if (!values || !Array.isArray(values)) {
        return "Keine Daten"; // Fallback für leere oder fehlerhafte Werte
      }

      // Extrahiere die `value`-Eigenschaften aus dem Array
      const valueArray = values.map((item) => item.value);

      // Formatiere die Ausgabe
      if (valueArray.length === 1) {
        return valueArray[0]; // Ein Wert
      } else if (valueArray.length === 2) {
        return valueArray.join(" und "); // Zwei Werte
      } else {
        return valueArray.slice(0, -1).join(", ") + " und " + valueArray[valueArray.length - 1]; // Mehr als zwei Werte
      }
    },
  },

  mounted() {
    console.log(this.item.variationProperties);
  },

  computed: {
    item() {
      return this.itemData || this.itemSlotData || this.itemDataRef;
    },

    itemSlotData: getSlotData("item-data"),

    /**
     * returns itemData.item.storeSpecial
     */
    storeSpecial() {
      return this.item.item.storeSpecial;
    },

    /**
     * returns itemData.texts
     */
    texts() {
      return this.item.texts;
    },

    itemPrice() {
      return this.$options.filters.specialOffer(this.item.prices.default.unitPrice.formatted, this.item.prices, "unitPrice", "formatted");
    },

    basePrice() {
      return this.item.prices.default.basePrice;
    },

    itemPriceGraduated() {
      let unitPrice;
      if (App.config.item.enableGraduatedPrices) {
        unitPrice = this.item.prices.graduatedPrices.length > 0 ? this.item.prices.graduatedPrices[0].unitPrice : this.item.prices.default.unitPrice;
      } else {
        unitPrice = this.item.prices.default.unitPrice;
      }

      return this.$options.filters.specialOffer(unitPrice.formatted, this.item.prices, "unitPrice", "formatted");
    },

    itemGraduatedPriceisCheapestSorting() {
      return !!this.item.item && this.item.item.salableVariationCount > 1 && !!this.$ceres.isCheapestSorting;
    },

    itemGraduatedPricesalableVariationCount() {
      return !!this.item.item && this.item.item.salableVariationCount == 1 && this.item.prices.graduatedPrices.length > 1 && App.config.item.enableGraduatedPrices;
    },

    itemSetPrice() {
      return this.$options.filters.currency(this.item.prices.default.price.value, this.item.prices.default.currency);
    },

    urlWithVariationId() {
      return !this.$ceres.config.item.showPleaseSelect || this.$ceres.initialPleaseSelect == 0 || this.forceUrlWithVariationId;
    },

    hasCrossPrice() {
      const hasRrpPrice = !!this.item.prices.rrp && this.item.prices.rrp.unitPrice.value > this.item.prices.default.unitPrice.value;

      const hasBeforePrice = !!this.item.prices.specialOffer && !!this.item.prices.default && this.item.prices.default.unitPrice.value > this.item.prices.specialOffer.unitPrice.value;

      return hasRrpPrice || hasBeforePrice;
    },

    ...mapState({
      showNetPrices: (state) => state.basket.showNetPrices,
    }),
  },
};
</script>
